import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from '../hooks/use-toast';
import { Phone, Mail, MapPin, Send, Clock, Loader2, CheckCircle } from 'lucide-react';
import MapDrawingTool from '../components/MapDrawingTool';
import SEO from '../components/SEO';

const Contact = () => {
  const [connectionType, setConnectionType] = useState('gas');
  const [mapImage, setMapImage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    postcode: '',
    service: '',
    connectionType: 'gas',
    capacity: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!privacyConsent) {
      toast({
        title: "Privacy Consent Required",
        description: "Please agree to the privacy policy before submitting.",
        variant: "destructive"
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Prepare form data with map image
      const submitData = {
        ...formData,
        mapImage: mapImage // Base64 encoded image
      };
      
      // Send to backend API
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData)
      });
      
      const result = await response.json();
      
      if (response.ok) {
        setIsSubmitted(true);
        toast({
          title: "Quote Request Sent!",
          description: result.message || "We'll get back to you within 24 hours.",
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          postcode: '',
          service: '',
          connectionType: 'gas',
          capacity: '',
          message: ''
        });
        setConnectionType('gas');
        setMapImage(null);
        setPrivacyConsent(false);
      } else {
        toast({
          title: "Error",
          description: result.detail || "Failed to send quote request. Please try again.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: "Error",
        description: "Failed to send quote request. Please try again or contact us directly.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleConnectionTypeChange = (value) => {
    setConnectionType(value);
    handleChange('connectionType', value);
    // Reset capacity when switching types
    handleChange('capacity', '');
  };

  return (
    <div>
      <SEO 
        title="Request a Quote | Multi Utility Connections & Metering | Green Frog Utilities"
        description="Request a quote for gas, water or electrical connections, metering, utility design and construction across mainland UK. WIRS, NERS and GIRS accredited."
        keywords="multi utility connections quote, gas connections quote, water connections quote, electrical connections quote, utility design UK, utility construction"
        canonical="/contact"
      />
      {/* Hero Section */}
      <section className="relative h-[400px] bg-gradient-to-r from-emerald-600 to-emerald-700 text-white">
        <div className="absolute inset-0 bg-black/20" />
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
          <div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
              Request a Quote
            </h1>
            <p className="text-xl text-emerald-100 max-w-2xl">
              Tell us about your project and we will come back to you within 24 hours. Gas, water and electrical connections, metering, utility design and construction across mainland UK.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Information */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Contact Information
                </h2>
                
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-emerald-50 rounded-lg">
                        <Phone className="h-6 w-6 text-emerald-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Phone</h3>
                        <a href="tel:+441675488644" className="text-gray-600 hover:text-emerald-600 transition-colors">
                          01675 488644
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-emerald-50 rounded-lg">
                        <Mail className="h-6 w-6 text-emerald-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                        <a href="mailto:contact@greenfrogutilities.co.uk" className="text-gray-600 hover:text-emerald-600 transition-colors break-all">
                          contact@greenfrogutilities.co.uk
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-emerald-50 rounded-lg">
                        <MapPin className="h-6 w-6 text-emerald-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-2">Office Location</h3>
                        <div className="space-y-3 text-sm">
                          <div>
                            <p className="font-medium text-gray-900">Office</p>
                            <p className="text-gray-600">
                              Unit 4 Ninian Park, Ninian Way<br />
                              Tamworth, Staffordshire B77 5ES
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-emerald-50 rounded-lg">
                        <Clock className="h-6 w-6 text-emerald-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Business Hours</h3>
                        <p className="text-gray-600 text-sm">
                          Monday - Friday<br />
                          8:30 AM - 5:00 PM
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Quote Form */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl">Request a Quote</CardTitle>
                  <CardDescription>
                    Please provide as much detail as possible to help us give you an accurate quote
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="name">Full Name *</Label>
                        <Input 
                          id="name" 
                          required 
                          value={formData.name}
                          onChange={(e) => handleChange('name', e.target.value)}
                          placeholder="John Smith"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email Address *</Label>
                        <Input 
                          id="email" 
                          type="email" 
                          required 
                          value={formData.email}
                          onChange={(e) => handleChange('email', e.target.value)}
                          placeholder="john@company.com"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone Number *</Label>
                        <Input 
                          id="phone" 
                          type="tel" 
                          required 
                          value={formData.phone}
                          onChange={(e) => handleChange('phone', e.target.value)}
                          placeholder="01675 488644"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="company">Company Name</Label>
                        <Input 
                          id="company" 
                          value={formData.company}
                          onChange={(e) => handleChange('company', e.target.value)}
                          placeholder="Your Company Ltd"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="postcode">Site Postcode *</Label>
                      <Input 
                        id="postcode" 
                        required 
                        value={formData.postcode}
                        onChange={(e) => handleChange('postcode', e.target.value)}
                        placeholder="B77 5ES"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="service">Service Required</Label>
                      <Select value={formData.service} onValueChange={(value) => handleChange('service', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="gas-connections">Gas Connections</SelectItem>
                          <SelectItem value="water-connections">Water Connections</SelectItem>
                          <SelectItem value="electrical-connections">Electrical Connections</SelectItem>
                          <SelectItem value="design">Design Services</SelectItem>
                          <SelectItem value="construction">Construction</SelectItem>
                          <SelectItem value="metering">Metering</SelectItem>
                          <SelectItem value="general">General Enquiry</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-3">
                      <Label>Connection Type *</Label>
                      <RadioGroup value={connectionType} onValueChange={handleConnectionTypeChange}>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="gas" id="gas" />
                          <Label htmlFor="gas" className="font-normal cursor-pointer">Gas Connection</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="water" id="water" />
                          <Label htmlFor="water" className="font-normal cursor-pointer">Water Connection</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="electrical" id="electrical" />
                          <Label htmlFor="electrical" className="font-normal cursor-pointer">Electrical Connection</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="multi" id="multi" />
                          <Label htmlFor="multi" className="font-normal cursor-pointer">Multi Utility Connection</Label>
                        </div>
                      </RadioGroup>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="capacity">
                        {connectionType === 'gas' ? 'Required Capacity (cubic meters/hour)' : 'Required Capacity'}
                      </Label>
                      <Input 
                        id="capacity" 
                        type="number"
                        step="0.01"
                        value={formData.capacity}
                        onChange={(e) => handleChange('capacity', e.target.value)}
                        placeholder={connectionType === 'gas' ? 'e.g., 100' : 
                                     connectionType === 'electrical' ? 'e.g., 500' :
                                     connectionType === 'water' ? 'e.g., 10' : 'e.g., 100'}
                      />
                      <p className="text-sm text-gray-500">
                        If known, please specify your required capacity. Leave blank if unsure.
                      </p>
                    </div>

                    {/* Map Drawing Tool */}
                    <MapDrawingTool onMapImageCapture={setMapImage} />

                    <div className="space-y-2">
                      <Label htmlFor="message">Additional Details</Label>
                      <Textarea 
                        id="message" 
                        rows={6}
                        value={formData.message}
                        onChange={(e) => handleChange('message', e.target.value)}
                        placeholder="Please provide any additional information about your project requirements, timeline, location, etc."
                      />
                    </div>

                    {/* Privacy Policy Consent */}
                    <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg border">
                      <Checkbox 
                        id="privacy-consent" 
                        checked={privacyConsent}
                        onCheckedChange={setPrivacyConsent}
                        className="mt-0.5"
                        data-testid="privacy-consent-checkbox"
                      />
                      <Label htmlFor="privacy-consent" className="text-sm text-gray-700 cursor-pointer leading-relaxed">
                        By pressing submit you agree to the{' '}
                        <Link to="/privacy-policy" className="text-emerald-600 hover:text-emerald-700 underline" target="_blank">
                          privacy policy
                        </Link>
                        . *
                      </Label>
                    </div>

                    {isSubmitted ? (
                      <div className="w-full p-6 bg-emerald-50 border border-emerald-200 rounded-lg text-center">
                        <CheckCircle className="h-12 w-12 text-emerald-600 mx-auto mb-3" />
                        <h3 className="text-lg font-semibold text-emerald-800 mb-2">Quote Request Sent Successfully!</h3>
                        <p className="text-emerald-700 mb-4">We&apos;ll get back to you within 24 hours.</p>
                        <Button 
                          type="button" 
                          variant="outline"
                          onClick={() => setIsSubmitted(false)}
                          className="border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                        >
                          Submit Another Request
                        </Button>
                      </div>
                    ) : (
                      <Button 
                        type="submit" 
                        size="lg" 
                        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={isSubmitting || !privacyConsent}
                        data-testid="submit-quote-button"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="h-5 w-5 mr-2" />
                            Submit Quote Request
                          </>
                        )}
                      </Button>
                    )}
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
